// src/errors/utils/error-throttler.ts

/**
 * Error Throttler Utility
 *
 * WHY: Prevent flooding users with duplicate error messages
 * WHAT: Deduplicates errors within a time window
 * BENEFIT:
 *   - Better user experience (no error spam)
 *   - Reduced noise in error monitoring
 *   - More meaningful error counts
 *
 * DESIGN DECISIONS:
 * 1. Uses error code + message hash for deduplication
 * 2. Configurable time window
 * 3. Automatic cleanup of old entries
 * 4. Can be disabled for debugging
 */

import { errorConfig } from "@/config";

/**
 * Cache entry for throttled errors
 */
interface ThrottleEntry {
  /** When error was first seen in window */
  firstSeen: number;
  /** Number of times error was seen */
  count: number;
}

/**
 * Error Throttler Class
 *
 * Prevents duplicate errors from being shown within a time window.
 */
class ErrorThrottler {
  /**
   * Cache of recently seen errors
   * Key: Error fingerprint
   * Value: Throttle entry with timing info
   */
  private cache: Map<string, ThrottleEntry> = new Map();

  /**
   * Interval ID for cleanup task
   */
  private cleanupInterval?: ReturnType<typeof setInterval>;

  constructor() {
    // Start cleanup interval if throttling is enabled
    if (errorConfig.throttle.enabled) {
      this.startCleanupInterval();
    }
  }

  /**
   * Generate fingerprint for error deduplication
   *
   * @param code - Error code
   * @param message - Error message
   * @param statusCode - HTTP status code
   */
  private generateFingerprint(
    code: string,
    message: string,
    statusCode?: number,
  ): string {
    // Use first 50 chars of message to handle dynamic content
    const truncatedMessage = message.slice(0, 50);
    return `${code}:${statusCode ?? 0}:${truncatedMessage}`;
  }

  /**
   * Check if error should be throttled
   *
   * @param error - Error object with code, message, statusCode
   * @returns true if error should be suppressed
   */
  shouldThrottle(error: {
    code: string;
    message: string;
    statusCode?: number;
  }): boolean {
    // If throttling is disabled, never throttle
    if (!errorConfig.throttle.enabled) {
      return false;
    }

    const fingerprint = this.generateFingerprint(
      error.code,
      error.message,
      error.statusCode,
    );

    const now = Date.now();
    const entry = this.cache.get(fingerprint);

    if (!entry) {
      // First occurrence, add to cache
      this.cache.set(fingerprint, {
        firstSeen: now,
        count: 1,
      });
      return false;
    }

    // Check if still within window
    const windowStart = now - errorConfig.throttle.windowMs;

    if (entry.firstSeen < windowStart) {
      // Window expired, reset
      this.cache.set(fingerprint, {
        firstSeen: now,
        count: 1,
      });
      return false;
    }

    // Within window, increment count
    entry.count++;

    // Throttle if exceeded max errors
    return entry.count > errorConfig.throttle.maxErrors;
  }

  /**
   * Get count of suppressed errors for a fingerprint
   */
  getSuppressedCount(error: {
    code: string;
    message: string;
    statusCode?: number;
  }): number {
    const fingerprint = this.generateFingerprint(
      error.code,
      error.message,
      error.statusCode,
    );

    const entry = this.cache.get(fingerprint);
    if (!entry) return 0;

    return Math.max(0, entry.count - errorConfig.throttle.maxErrors);
  }

  /**
   * Clear all cached entries
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Start periodic cleanup of old entries
   */
  private startCleanupInterval(): void {
    // Clean up every 30 seconds
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, 30000);
  }

  /**
   * Remove expired entries from cache
   */
  private cleanup(): void {
    const now = Date.now();
    const expireTime = now - errorConfig.throttle.windowMs * 2;

    for (const [fingerprint, entry] of this.cache.entries()) {
      if (entry.firstSeen < expireTime) {
        this.cache.delete(fingerprint);
      }
    }
  }

  /**
   * Stop cleanup interval (for testing/cleanup)
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    this.cache.clear();
  }
}

/**
 * Singleton throttler instance
 */
export const errorThrottler = new ErrorThrottler();
